import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, ArrowLeft, SignOut, ArrowClockwise, ClipboardText, ChatCircleText, Package, Receipt, EnvelopeSimple, Phone, MapPin, CalendarBlank, MagnifyingGlass, DownloadSimple, ChartLineUp, CurrencyDollar, Warning, TrendUp, X, CaretRight, Clock } from "@phosphor-icons/react";
import { adminLogin, adminGetQuotes, adminGetContacts, adminGetStoreOrders, adminGetSupplyOrders, adminUpdateSupplyOrderStatus, adminGetDashboard } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { BUSINESS } from "@/lib/data";

const TOKEN_KEY = "amwap_admin_token";

const fmtDate = (s) => {
  try { return new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return s || ""; }
};

const Chip = ({ children, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-600", green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700", amber: "bg-amber-100 text-amber-700",
  };
  return <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${tones[tone]}`}>{children}</span>;
};

const ContactLine = ({ icon: Icon, children }) =>
  children ? <span className="inline-flex items-center gap-1.5 text-sm text-slate-500"><Icon size={15} /> {children}</span> : null;

// searchable text + status accessor per tab
const searchText = (r, t) => {
  const f = {
    quotes: [r.name, r.email, r.phone, r.service, r.address, r.details],
    contacts: [r.name, r.email, r.phone, r.message],
    store: [r.product, r.category, r.name, r.email, r.phone, r.note],
    supply: [r.customer_email, r.id, ...((r.items || []).map((i) => i.name))],
  }[t] || [];
  return f.filter(Boolean).join(" ").toLowerCase();
};

export default function AdminInbox() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [storeOrders, setStoreOrders] = useState([]);
  const [supplyOrders, setSupplyOrders] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [detail, setDetail] = useState(null); // { type, data }
  const [range, setRange] = useState("all");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY); setToken("");
    setQuotes([]); setContacts([]); setStoreOrders([]); setSupplyOrders([]); setDashboard(null);
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [q, c, so, sup, dash] = await Promise.all([
        adminGetQuotes(t), adminGetContacts(t), adminGetStoreOrders(t), adminGetSupplyOrders(t), adminGetDashboard(t).catch(() => null),
      ]);
      setQuotes(q); setContacts(c); setStoreOrders(so); setSupplyOrders(sup); setDashboard(dash);
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Session expired."); logout(); }
      else toast.error("Could not load inbox.");
    } finally { setLoading(false); }
  }, [logout]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  useEffect(() => {
    if (token && tab === "overview") adminGetDashboard(token, range).then(setDashboard).catch(() => {});
  }, [range, token, tab]);

  const switchTab = (id) => { setTab(id); setSearch(""); setStatus("all"); };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const t = await adminLogin(passcode); localStorage.setItem(TOKEN_KEY, t); setToken(t); setPasscode(""); toast.success("Welcome back."); }
    catch { toast.error("Incorrect passcode."); }
    finally { setLoading(false); }
  };

  const activeData = useMemo(() => (
    { quotes, contacts, store: storeOrders, supply: supplyOrders }[tab] || []
  ), [tab, quotes, contacts, storeOrders, supplyOrders]);

  const statusOptions = useMemo(() => {
    if (tab === "contacts") return [];
    return Array.from(new Set(activeData.map((r) => r.status).filter(Boolean)));
  }, [tab, activeData]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activeData.filter((r) => {
      if (q && !searchText(r, tab).includes(q)) return false;
      if (status !== "all" && (r.status || "") !== status) return false;
      return true;
    });
  }, [activeData, search, status, tab]);

  const CSV_COLS = {
    quotes: [
      { key: "created_at", label: "Date" }, { key: "name", label: "Name" }, { key: "email", label: "Email" },
      { key: "phone", label: "Phone" }, { key: "service", label: "Service" }, { key: "address", label: "Address" },
      { key: "preferred_date", label: "Preferred date" }, { key: "preferred_time", label: "Preferred time" }, { key: "details", label: "Details" },
    ],
    contacts: [
      { key: "created_at", label: "Date" }, { key: "name", label: "Name" }, { key: "email", label: "Email" },
      { key: "phone", label: "Phone" }, { key: "message", label: "Message" },
    ],
    store: [
      { key: "created_at", label: "Date" }, { key: "product", label: "Product" }, { key: "category", label: "Category" },
      { key: "quantity", label: "Qty" }, { key: "price", label: "Price" }, { key: "name", label: "Name" },
      { key: "email", label: "Email" }, { key: "phone", label: "Phone" }, { key: "note", label: "Note" },
    ],
    supply: [
      { key: "created_at", label: "Date" }, { label: "Order #", get: (o) => o.id.slice(0, 8).toUpperCase() },
      { key: "customer_email", label: "Customer" }, { key: "amount", label: "Amount" }, { key: "status", label: "Status" },
      { label: "Items", get: (o) => (o.items || []).map((i) => `${i.quantity}x ${i.name}`).join(" | ") },
    ],
  };

  const exportCsv = () => {
    if (!visible.length) { toast.error("Nothing to export."); return; }
    downloadCsv(`amwap_${tab}`, CSV_COLS[tab], visible);
    toast.success(`Exported ${visible.length} row(s).`);
  };

  const FULFILL = ["paid", "packed", "shipped", "delivered", "cancelled"];
  const updateStatus = async (orderId, newStatus) => {
    const order = supplyOrders.find((o) => o.id === orderId);
    const willEmail = !!order?.customer_email && ["packed", "shipped", "delivered"].includes(newStatus);
    try {
      await adminUpdateSupplyOrderStatus(token, orderId, newStatus);
      setSupplyOrders((cur) => cur.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
      toast.success(willEmail ? `Marked ${newStatus} — customer notified.` : "Status updated.");
    } catch { toast.error("Could not update status."); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-navy-900 grain flex items-center justify-center px-5" data-testid="inbox-admin-login">
        <div className="relative z-10 w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="w-14 h-14 rounded-full bg-white grid place-items-center overflow-hidden"><img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" /></span>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center mb-5"><LockKey size={22} weight="fill" /></div>
            <h1 className="font-display font-black text-2xl text-navy-900 tracking-tight">Bookings & Orders</h1>
            <p className="text-slate-500 mt-2 text-sm">Enter your passcode to view quotes, bookings and orders.</p>
            <input type="password" data-testid="inbox-admin-passcode" value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="Passcode" autoFocus className="w-full mt-6 rounded-xl border border-slate-300 px-4 py-3.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600" />
            <button type="submit" disabled={loading || !passcode} data-testid="inbox-admin-login-submit" className="w-full mt-4 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">{loading ? "Checking…" : "Unlock"}</button>
            <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-navy-900"><ArrowLeft size={16} /> Back to website</Link>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: ChartLineUp, count: null },
    { id: "quotes", label: "Quotes & Bookings", icon: ClipboardText, count: quotes.length },
    { id: "contacts", label: "Messages", icon: ChatCircleText, count: contacts.length },
    { id: "store", label: "Service & Store Orders", icon: Package, count: storeOrders.length },
    { id: "supply", label: "Supply Orders (Paid)", icon: Receipt, count: supplyOrders.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inbox-admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center overflow-hidden"><img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" /></span>
            <span className="font-display font-extrabold text-navy-900">Bookings & Orders</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="inbox-back-site" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><ArrowLeft size={16} /> Website</Link>
            <button onClick={() => load(token)} data-testid="inbox-refresh" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><ArrowClockwise size={16} /> Refresh</button>
            <button onClick={logout} data-testid="inbox-admin-logout" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-4 py-2 text-sm font-bold hover:bg-navy-600"><SignOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => switchTab(t.id)} data-testid={`inbox-tab-${t.id}`} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === t.id ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}>
              <t.icon size={16} weight="fill" /> {t.label}{t.count != null ? ` (${t.count})` : ""}
            </button>
          ))}
        </div>

        {tab === "overview" ? (
          <Overview dashboard={dashboard} loading={loading} range={range} onRange={setRange} onViewSupply={() => switchTab("supply")} />
        ) : (
        <>
        {/* Search + status filter toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="inbox-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone…" className="w-full rounded-xl border border-slate-300 pl-10 pr-4 py-2.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600" />
          </div>
          {statusOptions.length > 0 && (
            <select data-testid="inbox-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-navy-900 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-navy-600 capitalize">
              <option value="all">All statuses</option>
              {statusOptions.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
            </select>
          )}
          <span data-testid="inbox-result-count" className="text-sm text-slate-500 font-medium">{visible.length} of {activeData.length}</span>
          <button onClick={exportCsv} data-testid="inbox-export-csv" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-navy-900 hover:bg-slate-100"><DownloadSimple size={16} weight="bold" /> Export CSV</button>
        </div>

        {loading && <p className="text-slate-500">Loading…</p>}

        {!loading && visible.length === 0 && (
          <p className="text-slate-500" data-testid="inbox-empty">
            {activeData.length === 0 ? "Nothing here yet." : "No records match your search or filter."}
          </p>
        )}

        {!loading && tab === "quotes" && visible.length > 0 && (
          <div className="space-y-3">
            {visible.map((q, i) => (
              <div key={q.id} data-testid={`inbox-quote-${i}`} onClick={() => setDetail({ type: "quotes", data: q })} className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:border-navy-300 hover:shadow-[0_8px_28px_rgb(10,17,40,0.07)] transition-all">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold text-navy-900">{q.name} <span className="font-normal text-slate-400 text-sm">· {q.service}</span></h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                      <ContactLine icon={EnvelopeSimple}>{q.email}</ContactLine>
                      <ContactLine icon={Phone}>{q.phone}</ContactLine>
                      <ContactLine icon={MapPin}>{q.address}</ContactLine>
                      <ContactLine icon={CalendarBlank}>{[q.preferred_date, q.preferred_time].filter(Boolean).join(" ")}</ContactLine>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 shrink-0">{fmtDate(q.created_at)} <CaretRight size={13} /></span>
                </div>
                {q.details && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap line-clamp-2">{q.details}</p>}
                {q.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {q.attachments.map((a, k) => <a key={k} href={a.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs font-bold text-navy-600 underline">{a.filename || `Attachment ${k + 1}`}</a>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "contacts" && visible.length > 0 && (
          <div className="space-y-3">
            {visible.map((c, i) => (
              <div key={c.id} data-testid={`inbox-contact-${i}`} onClick={() => setDetail({ type: "contacts", data: c })} className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:border-navy-300 hover:shadow-[0_8px_28px_rgb(10,17,40,0.07)] transition-all">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold text-navy-900">{c.name}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                      <ContactLine icon={EnvelopeSimple}>{c.email}</ContactLine>
                      <ContactLine icon={Phone}>{c.phone}</ContactLine>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 shrink-0">{fmtDate(c.created_at)} <CaretRight size={13} /></span>
                </div>
                {c.message && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap line-clamp-2">{c.message}</p>}
                {c.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.attachments.map((a, k) => <a key={k} href={a.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs font-bold text-navy-600 underline">{a.filename || `Attachment ${k + 1}`}</a>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "store" && visible.length > 0 && (
          <div className="space-y-3">
            {visible.map((o, i) => (
              <div key={o.id} data-testid={`inbox-store-${i}`} onClick={() => setDetail({ type: "store", data: o })} className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:border-navy-300 hover:shadow-[0_8px_28px_rgb(10,17,40,0.07)] transition-all">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-navy-900">{o.quantity > 1 ? `${o.quantity} × ` : ""}{o.product}</h3>
                      {o.category && <Chip tone="blue">{o.category}</Chip>}
                      {o.price && <Chip tone="green">{o.price}</Chip>}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                      <ContactLine icon={ClipboardText}>{o.name}</ContactLine>
                      <ContactLine icon={EnvelopeSimple}>{o.email}</ContactLine>
                      <ContactLine icon={Phone}>{o.phone}</ContactLine>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 shrink-0">{fmtDate(o.created_at)} <CaretRight size={13} /></span>
                </div>
                {o.note && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap line-clamp-2">{o.note}</p>}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "supply" && visible.length > 0 && (
          <div className="space-y-3">
            {visible.map((o, i) => (
              <div key={o.id} data-testid={`inbox-supply-${i}`} onClick={() => setDetail({ type: "supply", data: o })} className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:border-navy-300 hover:shadow-[0_8px_28px_rgb(10,17,40,0.07)] transition-all">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-navy-900">${Number(o.amount).toFixed(2)} {(o.currency || "usd").toUpperCase()}</h3>
                      <Chip tone={{ paid: "green", packed: "amber", shipped: "blue", delivered: "green", cancelled: "slate" }[o.status] || "green"}>{(o.status || "paid").charAt(0).toUpperCase() + (o.status || "paid").slice(1)}</Chip>
                      <span className="text-xs text-slate-400">#{o.id.slice(0, 8).toUpperCase()}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1.5">{o.customer_email || "Guest checkout"}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">{fmtDate(o.created_at)} <CaretRight size={13} /></span>
                    <select
                      data-testid={`supply-status-${i}`}
                      value={o.status || "paid"}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600 capitalize"
                    >
                      {FULFILL.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  {(o.items || []).map((it, k) => (
                    <div key={k} className="flex items-center justify-between text-sm text-slate-600">
                      <span>{it.quantity} × {it.name}</span>
                      <span className="font-semibold text-navy-900">${(it.unit_price * it.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </main>
      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} onStatus={updateStatus} />}
    </div>
  );
}

function Overview({ dashboard, loading, range, onRange, onViewSupply }) {
  if (loading && !dashboard) return <p className="text-slate-500">Loading…</p>;
  if (!dashboard) return <p className="text-slate-500" data-testid="overview-empty">Dashboard unavailable.</p>;
  const d = dashboard;
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const RANGES = [{ id: "all", label: "All time" }, { id: "week", label: "This week" }, { id: "month", label: "This month" }];
  const rangeLabel = { all: "all time", week: "this week", month: "this month" }[range] || "all time";
  const stats = [
    { label: "Revenue", value: money(d.revenue_total), icon: CurrencyDollar, tone: "text-green-600", sub: `${rangeLabel} · ${d.order_count} order${d.order_count !== 1 ? "s" : ""}` },
    { label: "Avg order", value: money(d.avg_order), icon: TrendUp, tone: "text-navy-600", sub: rangeLabel },
    { label: "To fulfill", value: d.pending_fulfillment, icon: Receipt, tone: "text-amber-600", sub: "paid / packed, not yet shipped" },
    { label: "Low stock", value: d.low_stock_count, icon: Warning, tone: d.out_of_stock_count ? "text-red-600" : "text-orange-600", sub: `${d.out_of_stock_count} out of stock` },
  ];
  return (
    <div data-testid="inbox-overview" className="space-y-6">
      <div className="flex items-center gap-2" data-testid="overview-range-toggle">
        {RANGES.map((r) => (
          <button key={r.id} onClick={() => onRange(r.id)} data-testid={`range-${r.id}`} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${range === r.id ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}>{r.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{s.label}</span>
              <s.icon size={20} weight="fill" className={s.tone} />
            </div>
            <p className="font-display font-black text-3xl text-navy-900 mt-3">{s.value}</p>
            <p className="text-xs text-slate-500 mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-display font-black text-lg text-navy-900 mb-4">Top products</h3>
          {(!d.top_products || d.top_products.length === 0) ? (
            <p className="text-sm text-slate-500">No sales yet.</p>
          ) : (
            <div className="space-y-3">
              {d.top_products.map((p, i) => {
                const max = d.top_products[0].revenue || 1;
                return (
                  <div key={i} data-testid={`top-product-${i}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-navy-900 truncate pr-3">{p.name}</span>
                      <span className="text-slate-500 shrink-0">{p.qty} sold · <span className="font-bold text-navy-900">${p.revenue.toFixed(2)}</span></span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-navy-900 rounded-full" style={{ width: `${Math.max(6, (p.revenue / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-black text-lg text-navy-900">Low / out of stock</h3>
            <button onClick={onViewSupply} className="text-xs font-bold text-navy-600 hover:underline">View orders →</button>
          </div>
          {(!d.low_stock_items || d.low_stock_items.length === 0) ? (
            <p className="text-sm text-slate-500">Everything's well stocked.</p>
          ) : (
            <div className="space-y-2">
              {d.low_stock_items.map((p, i) => (
                <div key={i} data-testid={`low-stock-${i}`} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                  <span className="text-navy-900 truncate pr-3">{p.name}{p.sku ? <span className="text-slate-400"> · {p.sku}</span> : ""}</span>
                  <span className={`font-bold shrink-0 ${p.stock_qty === 0 ? "text-red-600" : "text-orange-600"}`}>{p.stock_qty === 0 ? "Out of stock" : `${p.stock_qty} left`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="revenue-chart-card">
        <h3 className="font-display font-black text-lg text-navy-900 mb-1">Daily revenue</h3>
        <p className="text-xs text-slate-500 mb-4">{rangeLabel === "all time" ? "Last 30 days" : rangeLabel}</p>
        <RevenueChart daily={d.daily || []} money={money} />
      </div>
    </div>
  );
}

function RevenueChart({ daily, money }) {
  if (!daily || daily.length === 0) return <p className="text-sm text-slate-500">No sales data yet.</p>;
  const W = 900, H = 200, padX = 8, padY = 16;
  const max = Math.max(1, ...daily.map((d) => d.revenue));
  const n = daily.length;
  const x = (i) => padX + (n === 1 ? (W - 2 * padX) / 2 : (i * (W - 2 * padX)) / (n - 1));
  const y = (v) => H - padY - (v / max) * (H - 2 * padY);
  const pts = daily.map((d, i) => [x(i), y(d.revenue)]);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${H - padY} L${pts[0][0].toFixed(1)},${H - padY} Z`;
  const total = daily.reduce((s, d) => s + d.revenue, 0);
  const peak = daily.reduce((a, b) => (b.revenue > a.revenue ? b : a), daily[0]);
  const fmtDay = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return s; } };
  return (
    <div data-testid="revenue-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1E3A8A" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#revfill)" />
        <path d={line} fill="none" stroke="#0A1128" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r={daily[i].revenue > 0 ? 3.5 : 2} fill={daily[i].revenue > 0 ? "#0A1128" : "#CBD5E1"} />
            <title>{`${fmtDay(daily[i].date)}: ${money(daily[i].revenue)}`}</title>
          </g>
        ))}
      </svg>
      <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
        <span>{fmtDay(daily[0].date)}</span>
        <span className="font-semibold text-navy-900">Period total {money(total)}{peak.revenue > 0 ? ` · Peak ${fmtDay(peak.date)} (${money(peak.revenue)})` : ""}</span>
        <span>{fmtDay(daily[daily.length - 1].date)}</span>
      </div>
    </div>
  );
}

const STEPS = ["paid", "packed", "shipped", "delivered"];

function DetailModal({ detail, onClose, onStatus }) {
  const { type, data: r } = detail;
  const Row = ({ label, value }) => value ? (
    <div className="py-2 border-b border-slate-100 last:border-0">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="text-sm text-navy-900 mt-0.5 whitespace-pre-wrap break-words">{value}</p>
    </div>
  ) : null;

  const titles = { quotes: "Quote / Booking", contacts: "Message", store: "Service / Store Order", supply: "Supply Order" };

  return (
    <div className="fixed inset-0 z-[80] bg-navy-900/60 backdrop-blur-sm flex items-start justify-end" onMouseDown={onClose} data-testid="inbox-detail-modal">
      <div className="bg-white h-full w-full max-w-[520px] overflow-y-auto shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-navy-500">{titles[type]}</p>
            <h2 className="font-display font-black text-xl text-navy-900">{r.name || r.product || r.customer_email || `#${(r.id || "").slice(0, 8).toUpperCase()}`}</h2>
          </div>
          <button onClick={onClose} data-testid="inbox-detail-close" aria-label="Close" className="w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="px-6 py-4">
          {type === "quotes" && (<>
            <Row label="Name" value={r.name} /><Row label="Email" value={r.email} /><Row label="Phone" value={r.phone} />
            <Row label="Service" value={r.service} /><Row label="Address" value={r.address} />
            <Row label="Preferred" value={[r.preferred_date, r.preferred_time].filter(Boolean).join(" ")} />
            <Row label="Details" value={r.details} /><Row label="Received" value={fmtDate(r.created_at)} />
          </>)}
          {type === "contacts" && (<>
            <Row label="Name" value={r.name} /><Row label="Email" value={r.email} /><Row label="Phone" value={r.phone} />
            <Row label="Message" value={r.message} /><Row label="Received" value={fmtDate(r.created_at)} />
          </>)}
          {type === "store" && (<>
            <Row label="Product" value={r.product} /><Row label="Category" value={r.category} />
            <Row label="Quantity" value={String(r.quantity)} /><Row label="Price" value={r.price} />
            <Row label="Name" value={r.name} /><Row label="Email" value={r.email} /><Row label="Phone" value={r.phone} />
            <Row label="Note" value={r.note} /><Row label="Received" value={fmtDate(r.created_at)} />
          </>)}
          {type === "supply" && (<>
            <Row label="Order #" value={(r.id || "").slice(0, 8).toUpperCase()} />
            <Row label="Customer" value={r.customer_email || "Guest checkout"} />
            <Row label="Amount" value={`$${Number(r.amount).toFixed(2)} ${(r.currency || "usd").toUpperCase()}`} />
            <Row label="Placed" value={fmtDate(r.created_at)} />
            {r.tracking && <Row label="Tracking" value={r.tracking} />}
            <div className="py-3 border-b border-slate-100">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-2">Items</p>
              {(r.items || []).map((it, k) => (
                <div key={k} className="flex items-center justify-between text-sm text-slate-700 py-0.5">
                  <span>{it.quantity} × {it.name}</span>
                  <span className="font-semibold text-navy-900">${(it.unit_price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {/* Fulfillment tracker */}
            <div className="py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-3">Fulfillment</p>
              {(r.status || "paid") === "cancelled" ? (
                <Chip tone="slate">Cancelled</Chip>
              ) : (
                <div className="flex items-center">
                  {STEPS.map((s, i) => {
                    const cur = STEPS.indexOf(r.status || "paid");
                    const done = i <= cur;
                    return (
                      <div key={s} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${done ? "bg-navy-900 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</div>
                          <span className={`mt-1 text-[10px] font-bold uppercase ${done ? "text-navy-900" : "text-slate-400"}`}>{s}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className={`h-1 flex-1 mx-1 rounded ${i < cur ? "bg-navy-900" : "bg-slate-100"}`} />}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4">
                <label className="text-xs font-bold text-navy-900 mr-2">Update status</label>
                <select
                  data-testid="detail-status-select"
                  value={r.status || "paid"}
                  onChange={(e) => { onStatus(r.id, e.target.value); onClose(); }}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600 capitalize"
                >
                  {STEPS.concat("cancelled").map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </div>
            </div>
          </>)}
          {r.attachments?.length > 0 && (
            <div className="py-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-2">Attachments</p>
              <div className="flex flex-wrap gap-2">
                {r.attachments.map((a, k) => <a key={k} href={a.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-navy-600 underline">{a.filename || `Attachment ${k + 1}`}</a>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
